import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUploadQuote, useUploadQuoteBatch } from "@/api/queries";
import { useProcesses } from "@/api/queries/processes";
import {
  readLastModuleSlug,
  rememberLastModuleSlug,
} from "@/components/ProcessPicker";
import { ProcessSwitch } from "@/components/ProcessSwitch";
import { CompactUploadDropzone } from "@/components/upload/CompactUploadDropzone";
import { formatUploadError } from "@/lib/uploadErrors";
import { UploadErrorAdminInsight } from "@/components/UploadErrorAdminInsight";
import {
  hasConfigurator,
  maxUploadBytes,
  sortByLandingOrder,
} from "@/processes/registry";

/**
 * Empty-state upload entry-point for the cart / checkout.
 *
 * Replaces the hardcoded "Nahrát DXF" link to the homepage: the
 * customer uploads straight from the empty cart, into whatever the
 * tenant actually offers. Everything visible comes from the module
 * registry — the process switch lists the tenant's file-driven
 * modules, the accepted extensions and the format chips come from the
 * selected module's ``accepted_file_types``, and a tenant with no
 * file-driven module gets the "nothing to upload into" line instead of
 * a CTA that leads nowhere.
 *
 * Eligibility predicate mirrors Landing's ``switchProcesses`` (public
 * entry, available, file-driven, has a configurator, at most one
 * REQUIRED file slot) so the two entry-points can't drift apart; the
 * last-used module is shared with the homepage via ``ps.lastModule``.
 */
export function EmptyCartUpload({ lang }: { lang: string }): JSX.Element | null {
  const { t: tCart } = useTranslation("cart");
  const { t: tQuote } = useTranslation("quote");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();

  const processesQuery = useProcesses();
  const uploadProcesses = useMemo(
    () =>
      sortByLandingOrder(
        (processesQuery.data ?? []).filter(
          (p) =>
            p.is_available &&
            p.requires_file &&
            hasConfigurator(p) &&
            (p.file_inputs ?? []).filter((fi) => fi.required).length <= 1,
        ),
      ),
    [processesQuery.data],
  );

  const [selectedSlug, setSelectedSlug] = useState<string | null>(() =>
    readLastModuleSlug(),
  );
  const selected =
    uploadProcesses.find((p) => p.slug === selectedSlug) ??
    uploadProcesses[0] ??
    null;

  const upload = useUploadQuote({
    onSuccess: (quote) => navigate(`/${lang}/quote/${quote.id}`),
  });
  const batch = useUploadQuoteBatch({
    onSuccess: (res) => navigate(`/${lang}/quote-batch/${res.public_id}`),
  });
  const pending = upload.isPending || batch.isPending;
  const errorMessage = formatUploadError(
    batch.error ?? upload.error,
    tQuote,
    selected?.max_upload_mb,
  );

  // Registry still loading — render nothing rather than flashing the
  // "tenant offers nothing" line under the empty-cart text.
  if (processesQuery.isPending) return null;

  if (!selected) {
    return (
      <p className="mt-3 text-sm text-ink-3" data-testid="cart-empty-no-processes">
        {processesQuery.isError
          ? tCart("processesLoadError")
          : tCart("noProcessesAvailable")}
      </p>
    );
  }

  const onFilesChosen = (files: File[]) => {
    const processType = String(selected.name);
    if (files.length === 1) {
      upload.mutate({ file: files[0], lang, processType });
    } else {
      batch.mutate({ files, lang, processType });
    }
  };

  return (
    <div className="mt-5 space-y-3" data-testid="cart-empty-upload" aria-busy={pending}>
      {uploadProcesses.length > 1 && (
        <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
          <span className="text-sm font-medium text-ink-3">
            {tCommon("home.processSwitch.label")}
          </span>
          <ProcessSwitch
            processes={uploadProcesses}
            value={selected.slug}
            onChange={(slug) => {
              setSelectedSlug(slug);
              rememberLastModuleSlug(slug);
            }}
            disabled={pending}
          />
        </div>
      )}
      <div className="text-left">
        <CompactUploadDropzone
          disabled={pending}
          onFilesChosen={onFilesChosen}
          errorMessage={errorMessage}
          statusLabel={pending ? tQuote("upload.analyzing") : null}
          acceptedFileTypes={selected.accepted_file_types}
          maxBytes={maxUploadBytes(selected)}
          title={tQuote("upload.titleForProcess", {
            process: selected.display_name,
          })}
        />
        <UploadErrorAdminInsight error={batch.error ?? upload.error} lang={lang} />
      </div>
    </div>
  );
}
